import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../contexts/LanguageContext';
import { backendUrl } from '../config';
import './ViewClassPage.css';

const ViewClassPage = () => {
  const { classroomId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [classroom, setClassroom] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [removingUserId, setRemovingUserId] = useState(null);
  const [changingRoleUserId, setChangingRoleUserId] = useState(null);
  const [deletingClassroom, setDeletingClassroom] = useState(false);
  const [renewalDuration, setRenewalDuration] = useState('monthly'); // 'monthly', 'semester', 'yearly'
  const [leavingClassroom, setLeavingClassroom] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [renewingSubscription, setRenewingSubscription] = useState(false);
  const [updatingSettings, setUpdatingSettings] = useState(false);
  const [classroomNameInput, setClassroomNameInput] = useState('');
  const [classPasswordInput, setClassPasswordInput] = useState('');
  const [showSettingsEditor, setShowSettingsEditor] = useState(false);

  // Check if user is head by checking if they have 'Teacher' role in this classroom
  const [isHead, setIsHead] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null); // Track current user's role
  const [wordUsage, setWordUsage] = useState(null); // Classroom word generation usage

  useEffect(() => {
    const checkHeadStatus = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        setCurrentUserId(userId);

        if (!userId || !classroomId) {
          setIsHead(false);
          setCurrentUserRole(null);
          return;
        }

        // Check if user has 'Teacher' or 'Co-Teacher' role in this classroom
        const { data: memberData } = await supabase
          .from('classroom_members')
          .select('class_roll')
          .eq('classroom_id', classroomId)
          .eq('user_id', userId)
          .single();

        const role = memberData?.class_roll;
        setIsHead(role === 'Teacher' || role === 'Co-Teacher');
        setCurrentUserRole(role);
      } catch (err) {
        console.error('Error checking head status:', err);
        setIsHead(false);
        setCurrentUserRole(null);
      }
    };

    if (classroomId) {
      checkHeadStatus();
    }
  }, [classroomId]);

  useEffect(() => {
    const fetchClassroomData = async () => {
      if (!classroomId) {
        setError(t('classroomNotFound') || 'Classroom not found');
        setLoading(false);
        return;
      }

      try {
        // Fetch classroom details
        const { data: classroomData, error: classroomError } = await supabase
          .from('classrooms')
          .select('id, name, created_at, join_code, password, class_size, capacity, subscription_expires_at')
          .eq('id', classroomId)
          .single();

        if (classroomError) {
          throw classroomError;
        }

        if (!classroomData) {
          setError(t('classroomNotFound') || 'Classroom not found');
          setLoading(false);
          return;
        }

        setClassroom(classroomData);

        // Fetch classroom members
        const { data: membersData, error: membersError } = await supabase
          .from('classroom_members')
          .select('user_id, class_username, class_roll, joined_at')
          .eq('classroom_id', classroomId)
          .order('joined_at', { ascending: false });

        if (membersError) {
          throw membersError;
        }

        // Map members (use class_username and class_roll)
        if (membersData && membersData.length > 0) {
          const membersWithInfo = membersData.map(member => ({
            ...member,
            displayName: member.class_username || ('User ' + member.user_id.substring(0, 8)),
            role: member.class_roll || 'Student'
          }));
          setMembers(membersWithInfo);
        } else {
          setMembers([]);
        }
      } catch (err) {
        console.error('Error fetching classroom data:', err);
        setError(err.message || t('errorFetchingClassroom') || 'Error fetching classroom data');
      } finally {
        setLoading(false);
      }
    };

    fetchClassroomData();
  }, [classroomId, t]);

  // Fetch classroom word generation usage (only for Teachers/Co-Teachers)
  useEffect(() => {
    const fetchWordUsage = async () => {
      if (!classroomId || !currentUserId || !isHead) {
        return;
      }

      try {
        const { data, error } = await supabase.rpc('get_classroom_word_usage', {
          p_classroom_id: classroomId,
          p_user_id: currentUserId
        });

        if (error) {
          console.error('Error fetching word usage:', error);
          return;
        }

        if (data && !data.error) {
          setWordUsage(data);
        }
      } catch (err) {
        console.error('Error fetching word usage:', err);
      }
    };

    if (isHead && currentUserId) {
      fetchWordUsage();
    }
  }, [classroomId, isHead, currentUserId]);

  const handleRemoveMember = async (userId) => {
    if (!window.confirm(t('confirmRemoveStudent') || 'Are you sure you want to remove this student from the classroom?')) {
      return;
    }

    setRemovingUserId(userId);
    setError('');

    try {
      // Use database function to remove member and delete their SRS data atomically
      const { error: removeError } = await supabase.rpc('remove_classroom_member', {
        p_classroom_id: classroomId,
        p_user_id: userId
      });

      if (removeError) {
        throw removeError;
      }

      // Remove from local state
      setMembers(prev => prev.filter(m => m.user_id !== userId));
    } catch (err) {
      console.error('Error removing member:', err);
      setError(err.message || t('errorRemovingStudent') || 'Error removing student');
    } finally {
      setRemovingUserId(null);
    }
  };

  const handleChangeRole = async (userId, newRole, currentRole) => {
    // Special warning when promoting Co-Teacher to Teacher
    if (currentRole === 'Co-Teacher' && newRole === 'Teacher') {
      const confirmed = window.confirm(
        t('promoteCoTeacherWarning') ||
        'Warning: Promoting this Co-Teacher to Teacher will demote you to Co-Teacher. Are you sure you want to continue?'
      );
      if (!confirmed) {
        return;
      }
    }

    setChangingRoleUserId(userId);
    setError('');

    try {
      if (newRole === 'Teacher') {
        // --- PROMOTING TO TEACHER (OWNERSHIP TRANSFER) ---
        // Use the backend endpoint that handles Stripe cancellation + multiple DB updates
        const { data: { session } } = await supabase.auth.getSession();
        const accessToken = session?.access_token;

        const response = await fetch(`${backendUrl}/transfer-classroom-ownership`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            classroomId,
            targetUserId: userId,
            accessToken
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to transfer ownership');
        }
      } else {
        // --- OTHER ROLE CHANGES (RPC) ---
        const { error: roleError } = await supabase.rpc('change_classroom_member_role', {
          classroom_uuid: classroomId,
          target_user_id: userId,
          new_role: newRole
        });

        if (roleError) {
          throw roleError;
        }
      }

      // Refresh member list to show updated roles
      const { data: membersData, error: membersError } = await supabase
        .from('classroom_members')
        .select('user_id, class_username, class_roll, joined_at')
        .eq('classroom_id', classroomId)
        .order('joined_at', { ascending: false });

      if (membersError) {
        throw membersError;
      }

      // Update local state
      if (membersData && membersData.length > 0) {
        const membersWithInfo = membersData.map(member => ({
          ...member,
          displayName: member.class_username || ('User ' + member.user_id.substring(0, 8)),
          role: member.class_roll || 'Student'
        }));
        setMembers(membersWithInfo);
      } else {
        setMembers([]);
      }

      // If we promoted someone to Teacher, refresh current user's role
      if (newRole === 'Teacher') {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        if (userId) {
          const { data: memberData } = await supabase
            .from('classroom_members')
            .select('class_roll')
            .eq('classroom_id', classroomId)
            .eq('user_id', userId)
            .single();

          const role = memberData?.class_roll;
          setIsHead(role === 'Teacher' || role === 'Co-Teacher');
          setCurrentUserRole(role);
        }
      }
    } catch (err) {
      console.error('Error changing role:', err);
      setError(err.message || t('errorChangingRole') || 'Error changing member role');
    } finally {
      setChangingRoleUserId(null);
    }
  };

  const handleDeleteClassroom = async () => {
    if (currentUserRole !== 'Teacher') {
      setError(t('onlyTeachersCanDelete') || 'Only Teachers can delete classrooms');
      return;
    }

    const confirmed = window.confirm(
      t('confirmDeleteClassroom') ||
      `Are you sure you want to delete "${classroom?.name}"? This will permanently delete the classroom, all modules, word groups, words, and all student progress. This cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    setDeletingClassroom(true);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${backendUrl}/delete-classroom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          classroomId: classroomId,
          accessToken: session?.access_token
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete classroom');
      }

      // Redirect to dashboard after deletion
      navigate('/');
    } catch (err) {
      console.error('Error deleting classroom:', err);
      setError(err.message || t('errorDeletingClassroom') || 'Error deleting classroom');
    } finally {
      setDeletingClassroom(false);
    }
  };

  // Check if subscription is expired or expiring soon (within 7 days)
  const isSubscriptionExpired = () => {
    if (!classroom?.subscription_expires_at) return false;
    return new Date(classroom.subscription_expires_at) < new Date();
  };

  const handleSaveClassSettings = async () => {
    if (currentUserRole !== 'Teacher') {
      return;
    }
    setUpdatingSettings(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        setError(t('pleaseLogIn') || 'Please log in to continue.');
        setUpdatingSettings(false);
        return;
      }

      const payload = {
        classroomId,
        accessToken
      };

      const trimmedName = (classroomNameInput || '').trim();
      if (trimmedName && trimmedName !== classroom?.name) {
        payload.classroomName = trimmedName;
      }

      const trimmedPassword = (classPasswordInput || '').trim();
      if (trimmedPassword) {
        payload.newPassword = trimmedPassword;
      }

      const response = await fetch(`${backendUrl}/update-classroom-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t('errorUpdatingClassSettings') || 'Failed to update classroom settings');
      }

      if (data.classroom) {
        setClassroom(prev => ({
          ...prev,
          ...data.classroom
        }));
      }

      // On successful save, exit edit mode
      setShowSettingsEditor(false);
    } catch (err) {
      console.error('Error updating classroom settings:', err);
      setError(err.message || t('errorUpdatingClassSettings') || 'Failed to update classroom settings');
    } finally {
      setUpdatingSettings(false);
    }
  };

  const handleReshuffleCode = async () => {
    if (currentUserRole !== 'Teacher') {
      return;
    }
    setUpdatingSettings(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        setError(t('pleaseLogIn') || 'Please log in to continue.');
        setUpdatingSettings(false);
        return;
      }

      const payload = {
        classroomId,
        accessToken,
        reshuffleCode: true
      };

      const response = await fetch(`${backendUrl}/update-classroom-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t('errorUpdatingClassSettings') || 'Failed to update classroom settings');
      }

      if (data.classroom?.join_code) {
        setClassroom(prev => ({
          ...prev,
          join_code: data.classroom.join_code
        }));
      }
    } catch (err) {
      console.error('Error reshuffling classroom code:', err);
      setError(err.message || t('errorUpdatingClassSettings') || 'Failed to update classroom settings');
    } finally {
      setUpdatingSettings(false);
    }
  };

  const isSubscriptionExpiringSoon = () => {
    if (!classroom?.subscription_expires_at) return false;
    const expiresAt = new Date(classroom.subscription_expires_at);
    const sevenDaysFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    return expiresAt < sevenDaysFromNow && expiresAt > new Date();
  };

  const handleRenewSubscription = async (newCapacity = null) => {
    // Check if new plan has enough capacity for current students
    if (newCapacity && classroom && classroom.class_size > newCapacity) {
      alert(t('cannotDowngradePlan') || `You cannot switch to a plan with ${newCapacity} students because you currently have ${classroom.class_size} students. Please remove students first.`);
      return;
    }

    if (newCapacity && !window.confirm(`Are you sure you want to upgrade your classroom to ${newCapacity} students? You will be charged for the new plan rate.`)) {
      return;
    }

    if (!classroomId) return;

    setRenewingSubscription(true);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        setError(t('pleaseLogIn') || 'Please log in');
        setRenewingSubscription(false);
        return;
      }

      const response = await fetch(`${backendUrl}/renew-classroom-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          classroomId,
          plan: renewalDuration, // Use selected duration
          newCapacity: newCapacity, // Pass optional renewal capacity
          accessToken
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to initiate renewal');
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Error renewing subscription:', err);
      setError(err.message || t('failedToRenewSubscription') || 'Failed to renew subscription');
      setRenewingSubscription(false);
    }
  };

  const handleLeaveClassroom = async () => {
    if (currentUserRole === 'Teacher') {
      setError(t('teachersCannotLeave') || 'Teachers cannot leave classrooms. Delete the classroom instead.');
      return;
    }

    const confirmed = window.confirm(
      t('confirmLeaveClassroom') ||
      `Are you sure you want to leave "${classroom?.name}"? This will delete all your progress in this classroom.`
    );

    if (!confirmed) {
      return;
    }

    setLeavingClassroom(true);
    setError('');

    try {
      const { error: leaveError } = await supabase.rpc('leave_classroom', {
        classroom_uuid: classroomId
      });

      if (leaveError) {
        throw leaveError;
      }

      // Redirect to dashboard after leaving
      navigate('/');
    } catch (err) {
      console.error('Error leaving classroom:', err);
      setError(err.message || t('errorLeavingClassroom') || 'Error leaving classroom');
    } finally {
      setLeavingClassroom(false);
    }
  };

  // Check permissions
  if (loading || isHead === null) {
    return <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>{t('loading') || 'Loading...'}</div>;
  }

  if (isHead === false) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
        <h2>{t('accessDenied') || 'Access Denied'}</h2>
        <p>{t('onlyClassroomHeadCanView') || 'Only Teachers and Co-Teachers can view and manage the class.'}</p>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          {t('backToDashboard') || 'Back to Dashboard'}
        </button>
      </div>
    );
  }

  if (error && !classroom) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
        <h2>{t('error') || 'Error'}</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          {t('backToDashboard') || 'Back to Dashboard'}
        </button>
      </div>
    );
  }

  return (
    <div className="view-class-page">
      <button onClick={() => navigate('/')} className="btn btn-secondary btn-back">
        {t('backToDashboard') || 'Back to Dashboard'}
      </button>

        <div className="view-class-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h2>{classroom?.name || t('viewClass') || 'View Class'}</h2>
            {currentUserRole === 'Teacher' && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ marginTop: '0.5rem' }}
                onClick={() => setShowSettingsEditor(prev => {
                  const next = !prev;
                  if (next) {
                    setClassroomNameInput(classroom?.name || '');
                    setClassPasswordInput(classroom?.password || '');
                  }
                  return next;
                })}
              >
                {showSettingsEditor
                  ? (t('cancel') || 'Cancel')
                  : (t('editClassSettings') || 'Update Classroom')}
              </button>
            )}
            {classroom && (
              <div className="classroom-info">
                <div className="info-item">
                  <strong>{t('classroomCode') || 'Code'}:</strong>
                  <code>{classroom.join_code}</code>
                  {currentUserRole === 'Teacher' && showSettingsEditor && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      style={{ marginLeft: '0.5rem' }}
                      onClick={handleReshuffleCode}
                      disabled={updatingSettings}
                    >
                      {updatingSettings ? (t('reshuffling') || 'Reshuffling...') : (t('reshuffleCode') || 'Reshuffle Code')}
                    </button>
                  )}
                </div>
                <div className="info-item">
                  <strong>{t('classroomPassword') || 'Password'}:</strong>
                  {currentUserRole === 'Teacher' && showSettingsEditor ? (
                    <input
                      type="text"
                      value={classPasswordInput}
                      onChange={(e) => setClassPasswordInput(e.target.value)}
                      style={{ marginLeft: '0.5rem', padding: '4px 8px', borderRadius: 4, border: '1px solid #ddd', minWidth: 140 }}
                    />
                  ) : (
                    <code>{classroom.password}</code>
                  )}
                </div>
                <div className="info-item">
                  <strong>{t('students') || 'Students'}:</strong>
                  <span style={{ marginLeft: '0.5rem' }}>
                    {classroom.class_size || 0}
                    {classroom.capacity !== null && classroom.capacity !== undefined ? (
                      <> / {classroom.capacity}</>
                    ) : (
                      <> / ∞</>
                    )}
                  </span>
                </div>
                {currentUserRole === 'Teacher' && showSettingsEditor && (
                  <div className="info-item">
                    <strong>{t('classroomName') || 'Classroom Name'}:</strong>
                    <input
                      type="text"
                      value={classroomNameInput}
                      onChange={(e) => setClassroomNameInput(e.target.value)}
                      style={{ marginLeft: '0.5rem', padding: '4px 8px', borderRadius: 4, border: '1px solid #ddd', minWidth: 140 }}
                    />
                  </div>
                )}
                {currentUserRole === 'Teacher' && showSettingsEditor && (
                  <div className="info-item">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleSaveClassSettings}
                      disabled={updatingSettings}
                    >
                      {updatingSettings ? (t('saving') || 'Saving...') : (t('saveClassSettings') || 'Save Settings')}
                    </button>
                  </div>
                )}
                {classroom?.subscription_expires_at && (
                  <div className="billing-card" style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    backgroundColor: isSubscriptionExpired() ? '#fff5f5' : isSubscriptionExpiringSoon() ? '#fffbea' : '#f8fdf9',
                    borderRadius: '8px',
                    border: `1px solid ${isSubscriptionExpired() ? '#feb2b2' : isSubscriptionExpiringSoon() ? '#fef3c7' : '#c6f6d5'}`,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#333' }}>{t('subscriptionStatus') || 'Subscription Status'}</h4>
                      <span style={{
                        fontSize: '0.9rem',
                        padding: '4px 12px',
                        borderRadius: '16px',
                        fontWeight: '600',
                        backgroundColor: isSubscriptionExpired() ? '#dc3545' : isSubscriptionExpiringSoon() ? '#ffc107' : '#28a745',
                        color: isSubscriptionExpiringSoon() ? '#000' : '#fff'
                      }}>
                        {isSubscriptionExpired() ? t('expired') || 'Expired' : isSubscriptionExpiringSoon() ? t('expiresSoon') || 'Expires Soon' : t('active') || 'Active'}
                      </span>
                    </div>

                    <p style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', color: '#555' }}>
                      <strong>{t('expires') || 'Expires'}:</strong> {new Date(classroom.subscription_expires_at).toLocaleDateString()}
                    </p>

                    {currentUserRole === 'Teacher' && (isSubscriptionExpired() || isSubscriptionExpiringSoon()) && (
                      <div style={{ padding: '1rem', backgroundColor: 'rgba(255,255,255,0.5)', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.05)' }}>
                        <div style={{ marginBottom: '1rem' }}>
                          <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 'bold', color: '#666', textTransform: 'uppercase' }}>
                            {t('billingCycle') || 'Billing Cycle'}
                          </label>
                          <select
                            value={renewalDuration}
                            onChange={(e) => setRenewalDuration(e.target.value)}
                            className="renewal-select"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              borderRadius: '4px',
                              border: '1px solid #cbd5e0',
                              backgroundColor: '#fff'
                            }}
                          >
                            <option value="monthly">{t('monthly') || 'Monthly'}</option>
                            <option value="semester">{t('semester') || 'Semester (4 months)'}</option>
                            <option value="yearly">{t('yearly') || 'Yearly'}</option>
                          </select>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {/* Option 1: Renew Current Plan */}
                          <button
                            onClick={() => handleRenewSubscription()}
                            disabled={renewingSubscription}
                            className="btn btn-primary"
                            style={{ width: '100%', fontWeight: '600' }}
                          >
                            {renewingSubscription ? (t('processing') || 'Processing...') :
                              `${(t('renew') || 'Renew')} (${classroom.capacity} ${t('students') || 'students'})`}
                          </button>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                            {/* Option 2: Upgrade Logic */}
                            {classroom.capacity <= 5 && (
                              <button
                                onClick={() => handleRenewSubscription(35)}
                                disabled={renewingSubscription}
                                className="btn btn-success"
                                style={{ fontSize: '0.85rem', padding: '0.5rem', backgroundColor: '#38a169', borderColor: '#38a169', fontWeight: '600' }}
                              >
                                {renewingSubscription ? '...' : (t('upgradeToBasic') || 'Upgrade to 35 Students')}
                              </button>
                            )}

                            {classroom.capacity <= 35 && (
                              <button
                                onClick={() => handleRenewSubscription(150)}
                                disabled={renewingSubscription}
                                className="btn btn-success"
                                style={{ fontSize: '0.85rem', padding: '0.5rem', backgroundColor: '#2f855a', borderColor: '#2f855a', fontWeight: '600' }}
                              >
                                {renewingSubscription ? '...' : (t('upgradeToStandard') || 'Upgrade to 150 Students')}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {isHead && wordUsage && (
                  <div className="info-item" style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                    <strong>{t('wordGenerationLimit') || 'Word Generation Limit'}:</strong>
                    <span style={{ marginLeft: '0.5rem' }}>
                      {wordUsage.words_used || 0} / {wordUsage.monthly_limit || 1200} {t('wordsThisMonth') || 'words this month'}
                      {wordUsage.words_remaining !== undefined && (
                        <span style={{ color: wordUsage.words_remaining > 0 ? '#28a745' : '#dc3545', marginLeft: '0.5rem' }}>
                          ({wordUsage.words_remaining} {t('remaining') || 'remaining'})
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
            {currentUserRole === 'Teacher' && (
              <button
                className="btn btn-danger"
                onClick={handleDeleteClassroom}
                disabled={deletingClassroom}
              >
                {deletingClassroom ? (t('deleting') || 'Deleting...') : (t('deleteClassroom') || 'Delete Classroom')}
              </button>
            )}
            {(currentUserRole === 'Co-Teacher' || currentUserRole === 'Student') && (
              <button
                className="btn btn-danger"
                onClick={handleLeaveClassroom}
                disabled={leavingClassroom}
              >
                {leavingClassroom ? (t('leaving') || 'Leaving...') : (t('leaveClassroom') || 'Leave Classroom')}
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ color: 'red', margin: '1rem 0', textAlign: 'center' }}>
          {error}
        </div>
      )}

      <div className="members-section">
        <h3>{t('classroomMembers') || 'Classroom Members'}</h3>
        {members.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666', marginTop: '1rem' }}>
            {t('noMembersYet') || 'No members have joined this classroom yet.'}
          </p>
        ) : (
          <div className="members-list">
            {members.map(member => (
              <div key={member.user_id} className="member-item">
                <div className="member-info">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span className="member-email">{member.displayName}</span>
                    <span className="member-role" style={{
                      fontSize: '0.75rem',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      background: member.role === 'Teacher' ? '#6a994e' : member.role === 'Co-Teacher' ? '#386641' : '#888',
                      color: 'white',
                      fontWeight: '500'
                    }}>
                      {member.role === 'Teacher' ? (t('teacher') || 'Teacher') :
                        member.role === 'Co-Teacher' ? (t('coTeacher') || 'Co-Teacher') :
                          (t('student') || 'Student')}
                    </span>
                  </div>
                  <span className="member-joined">
                    {t('joined') || 'Joined'}: {new Date(member.joined_at).toLocaleDateString()}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {(() => {
                    // Only Teachers can change roles
                    if (currentUserRole !== 'Teacher' || member.user_id === currentUserId) {
                      return null;
                    }

                    return (
                      <div style={{ display: 'flex', gap: '0.25rem', flexDirection: 'column' }}>
                        {member.role === 'Student' && (
                          <button
                            onClick={() => handleChangeRole(member.user_id, 'Co-Teacher', member.role)}
                            disabled={changingRoleUserId === member.user_id}
                            className="btn btn-secondary btn-sm"
                            style={{ fontSize: '0.75rem', padding: '4px 8px' }}
                            title={t('promoteToCoTeacher') || 'Promote to Co-Teacher'}
                          >
                            {changingRoleUserId === member.user_id ? '...' : '→ Co-Teacher'}
                          </button>
                        )}
                        {member.role === 'Co-Teacher' && (
                          <>
                            <button
                              onClick={() => handleChangeRole(member.user_id, 'Teacher', member.role)}
                              disabled={changingRoleUserId === member.user_id}
                              className="btn btn-primary btn-sm"
                              style={{ fontSize: '0.75rem', padding: '4px 8px' }}
                              title={t('promoteToTeacher') || 'Promote to Teacher (will demote you to Co-Teacher)'}
                            >
                              {changingRoleUserId === member.user_id ? '...' : '→ Teacher'}
                            </button>
                            <button
                              onClick={() => handleChangeRole(member.user_id, 'Student', member.role)}
                              disabled={changingRoleUserId === member.user_id}
                              className="btn btn-secondary btn-sm"
                              style={{ fontSize: '0.75rem', padding: '4px 8px' }}
                              title={t('demoteToStudent') || 'Demote to Student'}
                            >
                              {changingRoleUserId === member.user_id ? '...' : '→ Student'}
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })()}

                  {(() => {
                    // Determine if remove button should be shown
                    // Teachers can remove anyone (except themselves)
                    // Co-Teachers can only remove Students
                    // Students cannot remove anyone

                    if (!currentUserRole) return null;

                    const showRemoveButton =
                      (currentUserRole === 'Teacher' && member.user_id !== currentUserId) ||
                      (currentUserRole === 'Co-Teacher' && member.role === 'Student');

                    if (!showRemoveButton) return null;

                    return (
                      <button
                        onClick={() => handleRemoveMember(member.user_id)}
                        disabled={removingUserId === member.user_id}
                        className="btn btn-danger btn-sm"
                      >
                        {removingUserId === member.user_id ? t('removing') || 'Removing...' : t('remove') || 'Remove'}
                      </button>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewClassPage;

